import { useQuery } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { formatFullDate } from '../api/calendar'
import { fetchWhatsNew, type WhatsNewEntry } from '../api/whatsNew'

/**
 * Everything new in the app for her, seen or not (D-731).
 *
 * The place a dismissed announcement is read again - reached from the dialog's
 * *See everything new* and, for a while after a release, from the bell. Grouped by
 * the date each was written for, newest first.
 *
 * *What's new in the app*, not *What's new*: `/news` is the studio's own news
 * (D-130), and the two must never be mistaken for each other.
 */
export default function WhatsNewView() {
  const { data, isPending, isError } = useQuery({ queryKey: ['whats-new'], queryFn: fetchWhatsNew })

  return (
    <div className="page-shell px-5 py-8">
      <header className="mb-5">
        <h1 className="font-display text-2xl">What&rsquo;s new in the app</h1>
        <p className="mt-1.5 max-w-[42rem] text-[13.5px] text-ink-3">
          New features you can use, newest first.
        </p>
      </header>

      {isPending && <p className="text-sm text-ink-3">Loading...</p>}

      {/* Never the empty sentence on a failure (D-209): that would be a statement about the app. */}
      {isError && (
        <p role="alert" className="text-sm text-crit">
          Could not load what&rsquo;s new. Try again in a moment.
        </p>
      )}

      {data && data.length === 0 && (
        <p className="rounded-2xl border border-line bg-surface px-5 py-8 text-center text-[13.5px] text-ink-3">
          Nothing new for you yet.
        </p>
      )}

      {data && data.length > 0 && (
        <div className="flex flex-col gap-6">
          {byRelease(data).map(([released, entries]) => (
            <section key={released}>
              <h2 className="mb-2 text-[12px] font-bold tracking-[0.11em] text-ink-3 uppercase">
                {formatFullDate(released)}
              </h2>
              <ul className="flex flex-col gap-2">
                {entries.map((entry) => (
                  <li key={entry.number} className="rounded-2xl border border-line bg-surface px-4 py-3">
                    <h3 className="text-[14px] font-semibold">{entry.title}</h3>
                    <p className="mt-1 max-w-[42rem] text-[13px] text-ink-2">{entry.body}</p>
                    {entry.wherePath && (
                      <Link to={entry.wherePath} className="mt-1.5 inline-block text-[13px] font-bold text-accent">
                        {entry.whereLabel} &rarr;
                      </Link>
                    )}
                  </li>
                ))}
              </ul>
            </section>
          ))}
        </div>
      )}
    </div>
  )
}

/** Entries grouped by release date, keeping the server's newest-first order. */
function byRelease(entries: WhatsNewEntry[]): [string, WhatsNewEntry[]][] {
  const groups = new Map<string, WhatsNewEntry[]>()
  for (const entry of entries) groups.set(entry.released, [...(groups.get(entry.released) ?? []), entry])
  return [...groups.entries()]
}
